import { z } from 'zod';

// The grammar a settings panel is declared in.
//
// It is the same one every element type already uses to say what its content is edited through — the
// builder's `SettingField` — lifted into shared so that something other than compiled TypeScript can
// declare a panel. A block is a JSON file read at boot, so its panel is input like any other: a bad
// control name has to be a startup warning naming the file, not a rail that draws nothing and explains
// itself in a console the merchant will never open.
//
// This is our `{% schema %}`. Shopify's sections carry one at the bottom of the Liquid, declaring the
// section's name and the controls its settings panel is built from; ours carries one beside the body it
// drives. What a control DOES is still the builder's, entirely — this file only says which ones exist.

/** Every control the inspector can draw. The builder's own `SettingControl` is checked against this. */
export const SettingControlEnum = z.enum([
  'text', 'textarea', 'number', 'toggle', 'color', 'image', 'url',
  'select', 'product', 'collection', 'menu', 'length', 'corners', 'align', 'icon', 'datetime', 'richtext',
  'group', 'list'
]);

/** One row of a `select`: the value that is stored, and the words the merchant picks it by. */
export const SelectOptionSchema = z.object({
  value: z.string().max(120),
  label: z.string().trim().min(1).max(120)
}).strict();

/** How many controls one panel — or one `group` inside it — may hold before it stops being scannable. */
export const MAX_SETTING_FIELDS = 40;

/** How deep a `group` inside a `group` may go. Two is a panel with sub-panels; three is a maze. */
export const MAX_SETTING_DEPTH = 3;

/** The key a setting is stored under: one word, so a settings path never has to be escaped. */
export const SettingKeySchema = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'A setting key is one word of letters, digits and underscores');

/** One control on a panel. Recursive, because a `group` and a `list` are made of the same thing. */
export type SettingFieldDto = {
  key: string;
  label: string;
  control: z.infer<typeof SettingControlEnum>;
  options?: z.infer<typeof SelectOptionSchema>[];
  fields?: SettingFieldDto[];
  min?: number;
  max?: number;
  help?: string;
  /** What an empty value means to the merchant — the first row a live picker offers, e.g. "All collections". */
  emptyLabel?: string;
};

/** Built once, its nested fields reaching back to it lazily, so a panel is never checked with schemas rebuilt per field. */
export const SettingFieldSchema: z.ZodType<SettingFieldDto, z.ZodTypeDef, unknown> = z.object({
  key: SettingKeySchema,
  label: z.string().trim().min(1).max(120),
  control: SettingControlEnum,
  options: z.array(SelectOptionSchema).max(MAX_SETTING_FIELDS).optional(),
  fields: z.array(z.lazy(() => SettingFieldSchema)).max(MAX_SETTING_FIELDS).optional(),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  help: z.string().trim().max(300).optional(),
  emptyLabel: z.string().trim().max(120).optional()
}).strict();

export type SettingControlName = z.infer<typeof SettingControlEnum>;
export type SelectOptionDto = z.infer<typeof SelectOptionSchema>;

// Measures how far a panel nests, so a `group` of a `group` of a `group` is refused where it is read.
export function settingDepth(fields: readonly SettingFieldDto[]): number {
  if (fields.length === 0) return 0;
  return 1 + Math.max(...fields.map((field) => settingDepth(field.fields ?? [])));
}

// Reports whether every key on one panel is its own, at every level — two "heading" rows write one value.
export function settingKeysUnique(fields: readonly SettingFieldDto[]): boolean {
  const seen = new Set<string>();
  for (const field of fields) {
    if (seen.has(field.key)) return false;
    seen.add(field.key);
    if (!settingKeysUnique(field.fields ?? [])) return false;
  }
  return true;
}
